// The gateway's stable error envelope {code, title, detail, remedy?}.
// Meta-tools return it as-is; every REST error body carries the same top-level `code`
// (apiFetch lifts it onto ApiError). `toEnvelope` normalises either into one shape so
// views branch on `code`, never on prose or HTTP status.
import type { ErrorCode, ErrorEnvelope } from './types'
import { isApiError } from './client'

/**
 * An envelope as the dashboard reads it. `code` is one of the documented ErrorCodes or,
 * for the few routes that send a more specific one (e.g. `disposable_email_domain`), that
 * string as-is.
 */
export type AppErrorEnvelope = Omit<ErrorEnvelope, 'code'> & { code: ErrorCode | (string & {}) }

/** Used only when no error body reached us (network failure, non-JSON proxy page). */
export const NETWORK_ERROR_CODE: ErrorCode = 'service_unavailable'

function text(v: unknown): string | undefined {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') {
    const d = (v as Record<string, unknown>).detail
    if (typeof d === 'string') return d
    try {
      return JSON.stringify(v)
    } catch {
      return undefined
    }
  }
  return undefined
}

/** Normalise a thrown ApiError, a meta-tool envelope or any error into an envelope. */
export function toEnvelope(err: unknown): AppErrorEnvelope {
  if (err && typeof err === 'object' && !(err instanceof Error)) {
    const o = err as Record<string, unknown>
    const title = typeof o.title === 'string' ? o.title : undefined
    const code = typeof o.code === 'string' && o.code ? o.code : NETWORK_ERROR_CODE
    return {
      code,
      title: title ?? 'Error',
      detail: text(o.detail) ?? title ?? 'Something went wrong.',
      ...(typeof o.remedy === 'string' && o.remedy ? { remedy: o.remedy } : {}),
    }
  }
  // fetch() rejected (offline, DNS, CORS) or something threw before a response.
  return {
    code: NETWORK_ERROR_CODE,
    title: 'Can’t reach the gateway',
    detail: err instanceof Error ? err.message : String(err),
  }
}

/** What the user can do about each code, shown under the gateway's own remedy. */
export const CODE_GUIDANCE: Record<ErrorCode, string> = {
  unauthorized: 'Your session or credential is no longer valid. Sign in again.',
  bad_request: 'Check the input and try again.',
  conflict: 'This conflicts with something that already exists. Change the input or update the existing item.',
  connector_denied: 'Blocked by this workspace’s connector policy (or outside the tool session’s scope). Ask a workspace owner or admin. No quota was used.',
  forbidden: 'Your role or plan doesn’t allow this. Ask a workspace owner or admin.',
  not_found: 'It doesn’t exist or isn’t available to you. Search again for the exact name.',
  connection_required: 'The toolkit has no usable credentials. Connect it in Toolkits, then retry.',
  upstream_error: 'The app’s own API failed or couldn’t be reached. Check the arguments and retry.',
  service_unavailable: 'The gateway is temporarily unavailable. Retry in a few seconds.',
  rate_limited: 'You hit the per-minute rate limit. Wait a moment, then retry.',
  quota_exceeded: 'The monthly quota is used up. Retrying soon won’t help: wait for the reset or upgrade your plan.',
  credits_exhausted: 'This workspace’s one-time free credits are used up. Retrying won’t help until the workspace moves to a paid plan. Searching tools still works.',
}

/** Guidance for a code, or undefined for a route-specific code the dashboard doesn't know. */
export function guidanceFor(code: string): string | undefined {
  return (CODE_GUIDANCE as Record<string, string>)[code]
}

/**
 * FastAPI's request-validation 422 body is `{detail: [{loc, msg, input, …}]}`. `input`
 * echoes what was sent (possibly a secret), so only `loc` and `msg` are ever shown.
 */
function validationMessage(detail: unknown): string | undefined {
  if (!Array.isArray(detail) || detail.length === 0) return undefined
  const parts = detail.map((d) => {
    const item = (d ?? {}) as { loc?: unknown; msg?: unknown }
    const msg = typeof item.msg === 'string' ? item.msg : 'Invalid value'
    const loc = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : ''
    return loc ? `${loc}: ${msg}` : msg
  })
  return parts.join('; ')
}

/** Message for an inline form error (keeps the gateway's detail; never echoes request bodies). */
export function errorMessage(err: unknown): string {
  if (isApiError(err)) {
    const validation = validationMessage(err.detail)
    if (validation) return validation
    const env = toEnvelope(err)
    return env.remedy ? `${env.detail} ${env.remedy}` : env.detail
  }
  return err instanceof Error ? err.message : 'Something went wrong.'
}
